import React, { useEffect, useMemo, useState } from "react";
import ZapierIntegration from "./ZapierIntegration";
import { fetchStatuses, createStatus, updateStatus, deleteStatus } from "./api";

const SettingsPage = ({ prioritySettings, setPrioritySettings }) => {
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [errorStatuses, setErrorStatuses] = useState("");

  const [newStatus, setNewStatus] = useState("");
  const [disableBadge, setDisableBadge] = useState(false);

  const loadStatuses = async () => {
    setLoadingStatuses(true);
    setErrorStatuses("");
    try {
      const data = await fetchStatuses();
      setStatuses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Errore fetchStatuses:", err);
      setErrorStatuses("Errore nel caricamento degli stati. Riprova.");
      setStatuses([]);
    } finally {
      setLoadingStatuses(false);
    }
  };

  useEffect(() => {
    loadStatuses();
  }, []);

  const sortedStatuses = useMemo(() => {
    // ordina per sort_order e name, così UI stabile anche se backend non ordina
    return [...statuses].sort((a, b) => {
      const ao = typeof a.sort_order === "number" ? a.sort_order : 0;
      const bo = typeof b.sort_order === "number" ? b.sort_order : 0;
      if (ao !== bo) return ao - bo;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [statuses]);

  const handleAddStatus = async () => {
    const name = newStatus.trim();
    if (!name) return;

    try {
      await createStatus({
        name,
        disables_badge: disableBadge,
        sort_order: 50,
      });

      setNewStatus("");
      setDisableBadge(false);
      await loadStatuses();
    } catch (err) {
      console.error("❌ Errore createStatus:", err);
      alert("Errore durante la creazione dello stato (forse esiste già).");
    }
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo stato?")) return;

    try {
      await deleteStatus(statusId);
      await loadStatuses();
    } catch (err) {
      console.error("❌ Errore deleteStatus:", err);
      // backend potrebbe rispondere 400 se lo stato è in uso
      const msg =
        err?.response?.data?.detail ||
        "Errore durante l'eliminazione (potrebbe essere uno stato in uso).";
      alert(msg);
    }
  };

  const toggleBadgeStatus = async (status) => {
    try {
      await updateStatus(status.id, {
        disables_badge: !status.disables_badge,
      });
      await loadStatuses();
    } catch (err) {
      console.error("❌ Errore updateStatus:", err);
      alert("Errore durante l'aggiornamento dello stato.");
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-6">⚙️ Impostazioni Menù CRM</h1>

      {/* ➕ Aggiunta nuovo stato */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2">➕ Aggiungi Nuovo Stato</h2>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <input
            type="text"
            placeholder="Nome nuovo stato..."
            className="border p-2 rounded w-full sm:w-64"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
          />

          <label className="flex items-center gap-2 select-none">
            <input
              type="checkbox"
              checked={disableBadge}
              onChange={() => setDisableBadge(!disableBadge)}
            />
            Disattiva badge
          </label>

          <button
            onClick={handleAddStatus}
            disabled={!newStatus.trim()}
            className={`px-4 py-2 rounded text-white ${
              newStatus.trim() ? "bg-blue-500 hover:bg-blue-600" : "bg-blue-300 cursor-not-allowed"
            }`}
          >
            ➕ Aggiungi
          </button>

          <button
            onClick={loadStatuses}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
            title="Ricarica stati"
          >
            🔄
          </button>
        </div>
      </div>

      {/* 📋 Stati esistenti */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-xl font-semibold">📋 Stati Esistenti</h2>
          {loadingStatuses && <span className="text-sm text-gray-500">Caricamento...</span>}
          {errorStatuses && <span className="text-sm text-red-500">{errorStatuses}</span>}
        </div>

        <ul className="space-y-2">
          {sortedStatuses.map((status) => (
            <li
              key={status.id}
              className="flex flex-col sm:flex-row sm:items-center gap-3 p-2 border rounded"
            >
              <div className="flex-1">
                <div className="font-medium">{status.name}</div>
                <div className="text-xs text-gray-500">
                  sort_order: {typeof status.sort_order === "number" ? status.sort_order : 0}
                </div>
              </div>

              <button
                onClick={() => toggleBadgeStatus(status)}
                className={`text-xs px-2 py-1 rounded text-white ${
                  status.disables_badge ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"
                }`}
              >
                {status.disables_badge ? "Badge Disattivato" : "Badge Attivo"}
              </button>

              <button
                onClick={() => handleDeleteStatus(status.id)}
                className="text-xs bg-gray-300 px-2 py-1 rounded hover:bg-gray-400"
              >
                ❌ Elimina
              </button>
            </li>
          ))}

          {!loadingStatuses && sortedStatuses.length === 0 && (
            <li className="text-sm text-gray-500">Nessuno stato disponibile.</li>
          )}
        </ul>
      </div>

      {/* 🔴🟡 Configura Priorità */}
      <div className="mb-10">
        <h2 className="text-xl font-semibold mb-2">🔴🟡 Configura Priorità Lead</h2>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <label className="w-48">Giorni per Urgente (🟡):</label>
            <input
              type="number"
              className="border p-2 rounded w-24"
              value={prioritySettings.urgentDays}
              onChange={(e) =>
                setPrioritySettings((prev) => ({
                  ...prev,
                  urgentDays: parseInt(e.target.value || "0", 10),
                }))
              }
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-48">Giorni per Scaduto (🔴):</label>
            <input
              type="number"
              className="border p-2 rounded w-24"
              value={prioritySettings.expiredDays}
              onChange={(e) =>
                setPrioritySettings((prev) => ({
                  ...prev,
                  expiredDays: parseInt(e.target.value || "0", 10),
                }))
              }
            />
          </div>
        </div>
      </div>

      {/* 🔗 Integrazione Zapier */}
      <div className="mt-10">
        <h2 className="text-xl font-semibold mb-2">🔗 Integrazione Zapier</h2>
        <ZapierIntegration />
      </div>
    </div>
  );
};

export default SettingsPage;
